/* Imports */
// Library
import { scaleLinear, symbol, symbolTriangle } from 'd3'
// Local
import './style.scss';
import Gradient from '../Gradient/gradient';
import { CLASSES as C, CONFIG, COLORS, SPACING } from '../../globals/constants';
import { colors } from '../../globals/helpers';


export default class PoleSlide {
  constructor(parent, data, type) {
    this.parent = parent
      .append("div")
      .attr("class", `${type}`)

    this.type = type
    this.data = data
  }

  /* Things that only happen once */
  init() {

    // config (smaller chart)
    this.HEIGHT = 150;

    // label & legend
    this.label = this.parent
      .append("div")
      .attr("class", `${C.LABEL}`)
      .style("transform", `translate(${- 9 * SPACING.linePadding}px,${(this.HEIGHT / 2) + 3 * SPACING.triangleSpacer.y}px)rotate(270deg)`)
      .style('width', `${this.HEIGHT}px`)
      .html(this.type)
    // set up gradient bar
    this.gradientWrapper = this.parent
      .append('div')
      .attr('class', `${C.GRADIENT}-${C.WRAPPER}`)
      .style('width', `${CONFIG.DIMS.width - 2 * CONFIG.DIMS.margin.x}px`)
      .style("transform", `translate(${0}px,${this.HEIGHT / 2}px)`)
      .style("opacity", 0)
      .call(div => div.transition()
        .duration(CONFIG.DURATION)
        .style("opacity", 0.5))

    new Gradient(this.gradientWrapper,
      50, COLORS.champagne, COLORS.green, CONFIG.DIMS.width - 4 * CONFIG.DIMS.margin.x, SPACING.axisHeight);

    // set up svg
    this.svg = this.parent
      .append('svg')
      .attr("id", "svg")
      .attr("width", CONFIG.DIMS.width - 2 * CONFIG.DIMS.margin.x)
      .attr("height", this.HEIGHT)
      .append('g')

    // get high and low values for control
    const onlyControls = this.data.map(({ control }) => control)
    const [start, end] = onlyControls
    this.diff = Math.abs(start - end)
    this.low = start > end ? end : start
    this.high = start < end ? end : start
    this.mid = this.low + ((this.high - this.low) / 2)
    this.xScale = scaleLinear([this.low, this.high], [20, CONFIG.DIMS.width - 3 * CONFIG.DIMS.margin.x])

    // draw triangle pointers
    this.triangles = this.svg
      .selectAll(`g.${C.AVERAGES}-${C.TRIANGLE}`)
      .data(this.data)
      .join(enter => {
        const triangle = enter
          .append("g")
          .style(
            "transform",
            ({ control }) =>
              `translate(${this.xScale(control === this.low ? this.low : this.high)}px,
      ${this.HEIGHT / 2 + SPACING.axisHeight + 2 * SPACING.triangleSpacer.y}px)`
          )

        return triangle
      })
      .attr("class", `${C.AVERAGES}-${C.TRIANGLE}`)
      // triangles
      .call(g => g
        .selectAll(`path.${C.BOTTOM}`)
        .data(d => [d])
        .join("path")
        .attr("class", `${C.BOTTOM}`)
        .attr("d", symbol().type(symbolTriangle).size(CONFIG.TRIANGLE_SIZE))
        .style("fill", ({ party }) => colors(party))
        .attr("stroke", ({ party }) => colors(party)))
      .call(g => g
        .selectAll(`path.${C.TOP}`)
        .data(d => [d])
        .join("path")
        .attr("class", `${C.TOP}`)
        .attr("d", symbol().type(symbolTriangle).size(CONFIG.TRIANGLE_SIZE))
        .style("fill", ({ party }) => colors(party))
        .attr("stroke", ({ party }) => colors(party))
        .attr("transform", `translate(0, ${-2 * SPACING.axisHeight - 2})rotate(180)`))
      // lines
      .call(g => g
        .selectAll("line")
        .data(d => [d])
        .join("line")
        .attr("x0", ({ control }) => control)
        .style("stroke", ({ party }) => colors(party))
        .style("stroke-width", 1.5)
        .attr("transform", `translate(0, -${1.5 * SPACING.triangleSpacer.y})`)
        .attr("opacity", 0.7)
        .attr("y1", -(SPACING.axisHeight + SPACING.triangleSpacer.y)))

    // grey difference lines at center of gradient
    this.lines = this.triangles
      .selectAll(`${C.DIFF}`)
      .data(d => [d])
      .join("line")
      .attr("class", `${C.DIFF}`)
      .style("stroke-width", 1.5)
      .attr("transform", ({ control }) => `translate(${control === this.low ? SPACING.linePadding : -SPACING.linePadding}, -${SPACING.axisHeight})`)
      .attr("x1", ({ control }) => this.xScale(this.mid) - this.xScale(control === this.low ? this.low : this.high) + (control === this.low ? -3 * SPACING.linePadding : 3 * SPACING.linePadding))
      .attr("y", 0)

    // difference number at center of gradient
    this.midText = this.triangles
      .selectAll(`${C.DIFF}`)
      .data([this.diff])
      .join("text")
      .attr("class", `${C.DIFF}`)
      .attr("transform", ({ control }) => `translate(${this.xScale(this.mid) - this.xScale(control === this.low ? this.low : this.high)}, -${SPACING.axisHeight})`)
      .text(d => d.toFixed(2))
      .attr('dy', "0.5em")


  }
  draw(toggleVal = "treatment") {
    const filteredData = this.data.map((d) => d[toggleVal])
    const [start, end] = filteredData
    const tDiff = Math.abs(start - end)
    const percent = tDiff / this.diff
    const translateAmt = this.diff * ((1 - percent) / 2)
    const low = start > end ? end : start
    const high = start < end ? end : start
    const mid = low + ((high - low) / 2)

    this.triangles
      .transition()
      .duration(CONFIG.DURATION)
      .style(
        "transform",
        (d) =>

          `translate(${this.xScale(d[toggleVal] === low ? this.low + translateAmt : this.high - translateAmt)}px, ${this.HEIGHT / 2 + SPACING.axisHeight + 2 * SPACING.triangleSpacer.y}px)`
      )

    this.lines
      .transition()
      .duration(CONFIG.DURATION)
      .attr("x1", (d) => this.xScale(mid) - this.xScale(d[toggleVal] === low ? low : high) + (d[toggleVal] === low ? -3 * SPACING.linePadding : 3 * SPACING.linePadding))

    this.midText
      .transition()
      .duration(CONFIG.DURATION)
      .attr("transform", (d) => `translate(${this.xScale(mid) - this.xScale(d[toggleVal] === low ? low : high)}, -${SPACING.axisHeight})`)
      .text(tDiff.toFixed(2))
  }

}

